import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { queryKeys } from 'lib/queryClient'
import {
  createManualRentalHistory,
  createRentalHistory,
  deleteManualRentalHistory,
  deleteRentalHistory,
  fetchRentalHistoryDetail,
  fetchRentalHistoryList,
  fetchRentalHistoryManagers,
  ManualRentalHistoryData,
  RentalHistoryData,
  updateManualRentalHistory,
  updateRentalHistory,
} from 'services/rentalHistoryApi'

import { translateErrorCodeToMessage } from 'app/constants/error_messages'
import * as snugNotifier from 'app/services/snugNotifier'

// TypeScript interfaces for hook parameters
interface CreateRentalHistoryVariables {
  rentalHistoryData: RentalHistoryData
  agencyGUID?: string
}

interface CreateManualRentalHistoryVariables {
  rentalHistoryData: ManualRentalHistoryData
}

interface UpdateManualRentalHistoryVariables {
  guidID: string
  rentalHistoryData: ManualRentalHistoryData
}

interface UpdateRentalHistoryVariables {
  id: string
  rentalHistoryData: RentalHistoryData
  agencyGUID?: string
}

/**
 * Hook to fetch all rental history entries
 */
export const useRentalHistoryList = (): UseQueryResult<any[], Error> => {
  return useQuery({
    queryKey: queryKeys.rentalHistory.list(),
    queryFn: fetchRentalHistoryList,
    select: (data) => {
      // Transform API data if needed
      return {
        ...data,
        // Add any additional computed properties
        totalEntries: data.length || 0,
      }
    },
  })
}

/**
 * Hook to fetch a specific rental history entry by ID
 */
export const useRentalHistoryDetail = (id: string) => {
  return useQuery({
    queryKey: queryKeys.rentalHistory.detail(id),
    queryFn: () => fetchRentalHistoryDetail(id),
    enabled: !!id, // Only run query if ID is available
  })
}

/**
 * Hook to create a new rental history entry (optimistic update)
 */
export const useCreateRentalHistory = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: CreateRentalHistoryVariables) =>
      createRentalHistory(variables.rentalHistoryData, variables.agencyGUID),

    // Optimistic update for better UX
    onMutate: async (variables: CreateRentalHistoryVariables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        queryKeys.rentalHistory.list(),
      )

      // Optimistically update to the new value
      queryClient.setQueryData(
        queryKeys.rentalHistory.list(),
        (old: any[] | undefined) => {
          const optimisticEntry = {
            ...variables.rentalHistoryData,
            id: `temp-${Date.now()}`, // Temporary ID
            isOptimistic: true,
          }
          return old ? [...old, optimisticEntry] : [optimisticEntry]
        },
      )

      // Return a context object with the snapshotted value
      return { previousData }
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (
      _err,
      _variables,
      context: { previousData?: any } | undefined,
    ) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          queryKeys.rentalHistory.list(),
          context.previousData,
        )
      }
      snugNotifier.error(translateErrorCodeToMessage(_err))
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })
    },

    onSuccess: (_data) => {
      // Additional success handling if needed
    },
    retry: false,
  })
}

/**
 * Hook to create a new manual rental history entry for applications
 */
export const useCreateManualRentalHistory = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: CreateManualRentalHistoryVariables) =>
      createManualRentalHistory(variables.rentalHistoryData),

    // Optimistic update for better UX
    onMutate: async (variables: CreateManualRentalHistoryVariables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        queryKeys.rentalHistory.list(),
      )

      // Optimistically update to the new value
      queryClient.setQueryData(
        queryKeys.rentalHistory.list(),
        (old: any[] | undefined) => {
          const optimisticEntry = {
            ...variables.rentalHistoryData,
            id: `temp-${Date.now()}`, // Temporary ID
            isOptimistic: true,
          }
          return old ? [...old, optimisticEntry] : [optimisticEntry]
        },
      )

      // Return a context object with the snapshotted value
      return { previousData }
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (
      _err,
      _variables,
      context: { previousData?: any } | undefined,
    ) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          queryKeys.rentalHistory.list(),
          context.previousData,
        )
      }
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })
    },

    onSuccess: (_data) => {
      // Additional success handling if needed
    },

    retry: false,
  })
}

/**
 * Hook to update an existing manual rental history entry
 */
export const useUpdateManualRentalHistory = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: UpdateManualRentalHistoryVariables) =>
      updateManualRentalHistory(variables.guidID, variables.rentalHistoryData),

    onMutate: async (variables: UpdateManualRentalHistoryVariables) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })

      // Snapshot the previous value for rollback
      const previousRentalHistory = queryClient.getQueryData(
        queryKeys.rentalHistory.list(),
      )

      // Optimistically update the cache
      queryClient.setQueryData(queryKeys.rentalHistory.list(), (old: any) => {
        if (!old) return old

        // If it's an array, update the matching item
        if (Array.isArray(old)) {
          return old.map((item: any) =>
            item.guidID === variables.guidID
              ? { ...item, ...variables.rentalHistoryData }
              : item,
          )
        }

        // If it's an object with a data property
        if (old.data && Array.isArray(old.data)) {
          return {
            ...old,
            data: old.data.map((item: any) =>
              item.guidID === variables.guidID
                ? { ...item, ...variables.rentalHistoryData }
                : item,
            ),
          }
        }

        return old
      })

      // Return context with previous value for rollback
      return { previousRentalHistory }
    },

    onError: (_err, _variables, context) => {
      // Rollback the optimistic update on error
      if (context?.previousRentalHistory) {
        queryClient.setQueryData(
          queryKeys.rentalHistory.list(),
          context.previousRentalHistory,
        )
      }
    },

    onSuccess: (data, variables: UpdateManualRentalHistoryVariables) => {
      // Update the cache with the actual server response
      queryClient.setQueryData(queryKeys.rentalHistory.list(), (old: any) => {
        if (!old) return old

        // If it's an array, update the matching item with server response
        if (Array.isArray(old)) {
          return old.map((item: any) =>
            item.guidID === variables.guidID ? data : item,
          )
        }

        // If it's an object with a data property
        if (old.data && Array.isArray(old.data)) {
          return {
            ...old,
            data: old.data.map((item: any) =>
              item.guidID === variables.guidID ? data : item,
            ),
          }
        }

        return old
      })
    },

    onSettled: () => {
      // Always refetch to ensure cache is in sync with server
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })
    },

    retry: false,
  })
}

/**
 * Hook to update an existing rental history entry
 */
export const useUpdateRentalHistory = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: UpdateRentalHistoryVariables) =>
      updateRentalHistory(
        variables.id,
        variables.rentalHistoryData,
        variables.agencyGUID,
      ),

    onSuccess: (_data, variables: UpdateRentalHistoryVariables) => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentalHistory.detail(variables.id),
      })
    },

    onError: (error) => {
      snugNotifier.error(translateErrorCodeToMessage(error))
    },
  })
}

/**
 * Hook to delete a rental history entry
 */
export const useDeleteRentalHistory = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteRentalHistory,

    // Optimistic update for better UX
    onMutate: async (id: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        queryKeys.rentalHistory.list(),
      )

      // Optimistically remove the entry
      queryClient.setQueryData(
        queryKeys.rentalHistory.list(),
        (old: any[] | undefined) => {
          return old ? old.filter((item: any) => item.id !== id) : []
        },
      )

      // Return a context object with the snapshotted value
      return { previousData }
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (_err, _id, context: { previousData?: any } | undefined) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          queryKeys.rentalHistory.list(),
          context.previousData,
        )
      }
      snugNotifier.error(translateErrorCodeToMessage(_err))
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })
    },

    retry: false,
  })
}

/**
 * Hook to delete a manual rental history entry
 */
export const useDeleteManualRentalHistory = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      rentalHistoryId,
      applicantGuid,
      agencyGuid,
    }: {
      rentalHistoryId: string
      applicantGuid: string
      agencyGuid: string
    }) => deleteManualRentalHistory(rentalHistoryId, applicantGuid, agencyGuid),

    // Optimistic update for better UX
    onMutate: async ({ rentalHistoryId }: { rentalHistoryId: string }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        queryKeys.rentalHistory.list(),
      )

      // Optimistically remove the entry
      queryClient.setQueryData(
        queryKeys.rentalHistory.list(),
        (old: any[] | undefined) => {
          return old
            ? old.filter((item: any) => item.guidID !== rentalHistoryId)
            : []
        },
      )

      // Return a context object with the snapshotted value
      return { previousData }
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (
      _err,
      _variables,
      context: { previousData?: any } | undefined,
    ) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          queryKeys.rentalHistory.list(),
          context.previousData,
        )
      }
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rentalHistory.list(),
      })
    },

    retry: false,
  })
}

/**
 * Hook to search for managers/agencies
 */
export const useRentalHistoryManagers = (searchValue: string) => {
  return useQuery({
    queryKey: queryKeys.rentalHistory.managers(searchValue),
    queryFn: () => fetchRentalHistoryManagers(searchValue),
    enabled: !!searchValue, // Only search when there is a search value
    staleTime: 30 * 1000, // Consider data stale after 30 seconds for search
  })
}
